import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useRecipe } from '../context/RecipeContext';
import landingBackground from '../images/landingbackground3.jpg';

const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  const handleGetStarted = () => {
    setMode('generate');
    navigate('/composer');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: { xs: '40vh', md: '50vh' },
          backgroundImage: `url(${landingBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(1px)',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 2,
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 1,
            }}
          >
            Your Questions Answered
          </Typography>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            textAlign: 'center',
            mb: 4,
            fontWeight: 'bold',
            color: 'primary.main'
          }}
        >
          Common Questions
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mb: 4,
            textAlign: 'center',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6,
            fontSize: { xs: '1rem', md: '1.1rem' }
          }}
        >
          Find answers to the most frequently asked questions about Create A Dish.
          If you can't find what you're looking for, feel free to contact us.
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">What is Create A Dish?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Create A Dish is an AI-powered recipe generation platform that helps you create personalized,
                delicious recipes based on your preferences, dietary restrictions, and available ingredients.
                Whether you're a beginner or an experienced cook, our tool makes cooking fun and accessible.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">How does the recipe generation work?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Our AI analyzes your input preferences, including cuisine type, dietary restrictions,
                available ingredients, skill level, and cooking time. It then generates customized recipes
                that match your criteria, complete with step-by-step instructions, ingredient lists,
                and nutritional information.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Is Create A Dish free to use?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Yes! Create A Dish is completely free to use. You can generate unlimited recipes,
                save your favorites, and access all our features without any subscription fees.
                We believe great cooking should be accessible to everyone.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Can I save my recipes?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Absolutely! You can save any recipe you generate to your personal collection.
                Your saved recipes are stored securely and can be accessed anytime from your account.
                You can also organize them by categories and add personal notes.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">What dietary restrictions do you support?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                We support a wide range of dietary preferences and restrictions, including:
                vegetarian, vegan, gluten-free, dairy-free, keto, paleo, low-carb, Mediterranean,
                and many more. You can also specify allergies and customize your preferences further.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">How accurate are the nutritional facts?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Our nutritional information is estimated based on standard ingredient databases
                and cooking methods. While we strive for accuracy, these are approximations and
                may vary based on specific brands, preparation methods, and portion sizes.
                For precise nutritional needs, we recommend consulting with a healthcare professional.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Can I share recipes with others?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Yes! You can share your favorite recipes with friends and family through social media,
                email, or by generating a shareable link. This makes it easy to collaborate on meals
                or spread your culinary creations.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">What if I don't have all the ingredients?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                No problem! Our AI can suggest ingredient substitutions based on what you have available.
                You can also regenerate recipes with different ingredients or adjust serving sizes
                to match what you have on hand.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Is my data safe and private?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Yes, we take your privacy seriously. Your personal information and recipe preferences
                are encrypted and stored securely. We never share your data with third parties without
                your consent. Please review our Privacy Policy for more details.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">How can I contact support?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                You can reach our support team at{' '}
                <Typography component="span" sx={{ fontWeight: 'bold' }}>info@aisuretech.com</Typography>.
                We're here to help with any questions, feedback, or technical issues you might have.
                We typically respond within 24 hours.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h5"
            component="h3"
            sx={{
              mb: 3,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            Ready to Create Your Perfect Dish?
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<StarIcon />}
            onClick={handleGetStarted}
            sx={{
              minWidth: 200,
              fontSize: '1.1rem',
              py: 1.5,
              px: 4,
            }}
          >
            Get Started
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            © 2025 Create A Dish. Made with ❤️ for food lovers everywhere.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FAQ;